import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, Pipe, PipeTransform } from '@angular/core';
import { AssetVersionDto } from '@picflow/types';
import { assetSizes, formatsWithTransparency, maxAnimatedGifSize } from 'src/app/consts';
import { getMaxWebGlTextureSize } from 'src/app/gallery/lib/max-webgl-texture-size';
import { calcBaseUrl } from 'src/app/lib/calc-base-url';
import { calcResizedUrl } from 'src/app/lib/calc-resized-url';
import { devicePixelRatio } from 'src/app/lib/device-pixel-ratio';
import { loadImg } from 'src/app/lib/load-img';
import { AppConfigService } from 'src/app/providers/config.service';
import { AssetPreGenerateService } from 'src/app/services/asset-pre-generate.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { UrlAutoUpscaleService } from 'src/app/services/url-auto-upscale.service';

const canvasAssetSizes = [720, 1280, 2560, 3840, 99999999999999];
const lowResMaxSize = 1920; // Don't use low res assets for resolutions bigger than this
const lowResMinSize = 641; // Don't use low res assets for resolutions smaller than this
const highQuality = 85;
const lowQuality = 75;

@Pipe({
  name: 'assetVersionUrl',
  standalone: true,
})
@Injectable({
  providedIn: 'root',
})
export class AssetVersionUrlPipe implements PipeTransform {
  constructor(
    private config: AppConfigService,
    @Inject(PLATFORM_ID) private platformId: string,
    private urlUpscaleService: UrlAutoUpscaleService,
    private assetPreGenerateService: AssetPreGenerateService,
    private featureFlags: FeatureFlagsService
  ) {}

  private canUseLowQuality(width: number, height: number, aspect: number) {
    if (isPlatformServer(this.platformId)) return false;
    if (devicePixelRatio() <= 1) return false; // No low quality on low res devices
    // No low quality when asset size is big
    if (aspect >= 1 && width > lowResMaxSize) return false;
    if (aspect <= 1 && height > lowResMaxSize) return false;
    if (aspect >= 1 && width < lowResMinSize) return false;
    if (aspect <= 1 && height < lowResMinSize) return false;
    return true;
  }

  private getTargetDimensions(
    targetWidth: number,
    sourceWidth: number,
    sourceHeight: number,
    options: { forCanvas?: boolean; allowLowRes?: boolean; halfQuality?: boolean; allowDouble?: boolean } = {}
  ) {
    if (sourceWidth === 0) throw 'Invalid source width';
    if (sourceHeight === 0) throw 'Invalid source height';
    if (targetWidth <= 0) return { width: 0, height: 0, quality: 0 };

    // Increase resolution on high res displays
    if (isPlatformBrowser(this.platformId) && devicePixelRatio() >= 2 && options.allowDouble) targetWidth *= 2;

    if (options.halfQuality) targetWidth /= 3;

    const aspect = sourceHeight / sourceWidth;
    let width = sourceWidth;
    let height = sourceHeight;

    const sizes = options.forCanvas ? canvasAssetSizes : assetSizes;
    let index = sizes.length - 1;
    while (sizes[index - 1] >= targetWidth * 0.9) index -= 1;
    if (index < 0) index = 0;
    width = sizes[index];
    height = width * aspect;

    if (width > sourceWidth || height > sourceHeight) {
      width = sourceWidth;
      height = sourceHeight;
    }

    // Reducing size when needed (used to avoid webgl limit)
    // TODO: implement SplitTexture to allow bigger textures to be loaded.
    if (options.forCanvas) {
      const maxSize = getMaxWebGlTextureSize();
      if (aspect <= 1 && width > maxSize) {
        width = maxSize;
        height = width * aspect;
        console.warn('Asset resolution limited by MAX_TEXTURE_SIZE');
      }
      if (aspect >= 1 && height > maxSize) {
        height = maxSize;
        width = height / aspect;
        console.warn('Asset resolution limited by MAX_TEXTURE_SIZE');
      }
    }

    // Choose correct quality for resolution and pixel density
    const quality = options.allowLowRes && this.canUseLowQuality(width, height, aspect) ? lowQuality : highQuality;

    return { width: Math.round(width), height: Math.round(height), quality };
  }

  async get(
    uuid: string,
    targetWidth: number,
    sourceWidth: number,
    sourceHeight: number,
    extension: string,
    options: {
      forCanvas?: boolean;
      allowLowRes?: boolean;
      halfQuality?: boolean;
      allowDouble?: boolean;
    } = {}
  ): Promise<string> {
    const { width, height, quality } = this.getTargetDimensions(targetWidth, sourceWidth, sourceHeight, options);

    if (isNaN(width) || isNaN(height) || isNaN(quality)) {
      // eslint-disable-next-line no-debugger
      debugger;
      console.error('Invalid width, height or quality');
      return '';
    }

    if (!width || !height) return '';
    if (!uuid) return '';

    uuid = uuid.split('|')[0];

    const out = this.urlUpscaleService.get(uuid, width, height, quality);

    const canUseTransparentVersion = formatsWithTransparency.includes(extension);

    // We don't want to resize to the base size
    if (out.width >= sourceWidth && !canUseTransparentVersion) return calcBaseUrl(this.config.assetsEndpoint, uuid);

    const resizedUrl = calcResizedUrl(
      this.config.assetsEndpoint,
      uuid,
      `${out.width}x${out.height}q${out.quality}`,
      canUseTransparentVersion
    );
    await this.assetPreGenerateService.whenNotGenerating(resizedUrl);
    return resizedUrl;
  }

  async getGeneric(extension: string, width: number): Promise<string> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = width * 2;
    canvas.height = width * 2;
    ctx.fillStyle = '#DEE8F1';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const image = await loadImg('/assets/icons/document-image-grey-50.svg');

    const imageWidth = devicePixelRatio() > 1 ? 64 : 32;
    const imageHeight = image.height * (imageWidth / image.width);
    ctx.drawImage(
      image,
      canvas.width / 2 - imageWidth / 2,
      canvas.height / 2 - imageHeight / 2,
      imageWidth,
      imageHeight
    );

    ctx.font = `900 ${devicePixelRatio() > 1 ? 22 : 11}px AkkuratLL`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (width > 200) {
      const text = extension.toUpperCase();
      const measurement = ctx.measureText(text.toUpperCase());
      const offsetX = (measurement.actualBoundingBoxRight - measurement.actualBoundingBoxLeft) / 2;
      const offsetY = (imageHeight / 2) * 1.2;

      ctx.fillStyle = '#8595a8';
      ctx.fillText(text, canvas.width / 2 + offsetX, canvas.height / 2 + offsetY);
    }

    return canvas.toDataURL('png');
  }

  async transform(
    version: AssetVersionDto,
    uuid: string,
    status: string,
    watermark_uuid: string,
    width: number,
    allowLowRes = false,
    watermark: boolean,
    halfQuality = false,
    allowDouble = true,
    forCanvas = false
  ): Promise<string> {
    if (!version) return '';
    if (status === 'pending') return '';
    const _uuid = watermark ? watermark_uuid || uuid : uuid;
    const extension = version.asset_name?.split('.').pop();

    const data = version.data;
    if (!data || status === 'failed') {
      return this.getGeneric(extension, width);
    }

    if (['gif', 'webp'].includes(extension) && !watermark_uuid) {
      if (version.size < maxAnimatedGifSize) {
        return this.config.assetsEndpoint + '/images/' + version.original_key;
      }
    }

    return this.get(_uuid, width, data.width, data.height, extension, {
      allowLowRes,
      halfQuality,
      allowDouble,
      forCanvas,
    });
  }
}
